/**
 * D3's own URL keys (spec §4.6, R4): sort, format and all, on /creatives only, in the URL and never remembered.
 * useDashboardFilters stays as it is; this hook sits beside it.
 *
 * - Whenever the URL does not carry the canonical form of the values in force (a spelled-out default, an unknown
 *   value, a repeated key), ONE replace writes it, with the shared filters in force, as useScopeArrival does.
 *   useDashboardFilters may rewrite the URL in the same tick, and react-router's setter builds on the last render's
 *   URL, so whichever write lands last carries both. The check is by value, so nothing loops: the next render
 *   finds the URL carrying both and writes nothing.
 * - setCreative REPLACES the entry, as every dashboard filter does: Back leaves the page.
 *
 * One write per event: react-router's functional setter builds on the search params of the last render, so two writes
 * in one tick keep only the second (as useDashboardFilters states it).
 *
 * @module hooks/useCreativeUrl
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  carriesCreativeUrl, parseCreativeUrl, writeCreativeUrl, writeShared, type CreativeUrlFilters, type SharedFilters,
} from '@/services/dashboardFilters';

interface CreativeUrl {
  creative: CreativeUrlFilters;
  setCreative: (patch: Partial<CreativeUrlFilters>) => void;
}

const REPLACE = { replace: true } as const;

export const useCreativeUrl = (shared: SharedFilters): CreativeUrl => {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const creative = useMemo(() => parseCreativeUrl(new URLSearchParams(search)), [search]);
  const { sort, format, showAll } = creative;
  const { period, objective, currency } = shared;

  useEffect(() => {
    const current = new URLSearchParams(search);
    const inForce = { sort, format, showAll };
    if (!carriesCreativeUrl(current, inForce)) {
      setParams(writeCreativeUrl(writeShared(current, { period, objective, currency }), inForce), REPLACE);
    }
  }, [search, sort, format, showAll, period, objective, currency, setParams]);

  const setCreative = useCallback((patch: Partial<CreativeUrlFilters>) => {
    setParams((prev) => writeCreativeUrl(prev, { ...parseCreativeUrl(prev), ...patch }), REPLACE);
  }, [setParams]);

  return useMemo(() => ({ creative, setCreative }), [creative, setCreative]);
};
